import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Phone, CalendarClock } from "lucide-react";
import { BRAND, errMsg, inr } from "./constants";
import { BrandMark } from "./BrandingPanel";

const SERVICES = { food: "🍱 Food Assist", city_explore: "🗺️ City Explore", luggage: "🧳 Luggage Help", cab: "🛺 Need Cab or Auto", networking: "🤝 Business Networking", flatmate: "🏠 Flatmate Interested" };

function TaxBreakdown({ b }) {
  if (!b.tax) return <div className="flex justify-between"><span>Total</span><b>{inr(b.amount_inr)}</b></div>;
  return (
    <div className="space-y-0.5" data-testid={`my-booking-tax-${b.id}`}>
      <div className="flex justify-between"><span>Base</span><span>{inr(b.base_inr)}</span></div>
      {Object.entries(b.tax).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k.toUpperCase()}</span><span>{inr(v)}</span></div>)}
      <div className="flex justify-between font-bold"><span>Total</span><span>{inr(b.total_inr)}</span></div>
    </div>
  );
}

function ExtendBox({ b, authFetch, API, onDone }) {
  const [date, setDate] = useState("");
  const [msg, setMsg] = useState("");
  const open = b.extend_requests.find((x) => ["pending", "countered"].includes(x.status));
  const send = async () => {
    try { await authFetch({ method: "post", url: `${API}/homestay/bookings/${b.id}/extend`, data: { new_check_out: date, message: msg || null } }); toast.success("Extend request sent to manager"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not send")); }
  };
  const accept = async () => {
    try { await authFetch({ method: "post", url: `${API}/homestay/extend/${open.id}/accept-counter` }); toast.success("Extension confirmed"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not accept")); }
  };
  if (open?.status === "pending") return <div data-testid={`extend-pending-${b.id}`} className="text-xs font-bold" style={{ color: BRAND.orange }}>Extend request to {open.new_check_out} is with the manager…</div>;
  if (open?.status === "countered") return (
    <div data-testid={`extend-counter-${b.id}`} className="text-xs rounded-xl p-2" style={{ background: "#FFF7ED" }}>
      Manager offers check-out <b>{open.counter_check_out}</b> for +{inr(open.counter_amount_inr)}{open.note ? ` · ${open.note}` : ""}
      <button data-testid={`extend-accept-${b.id}`} onClick={accept} className="ml-2 rounded-full px-3 py-1 font-bold text-white" style={{ background: BRAND.green }}>Accept</button>
    </div>
  );
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`extend-form-${b.id}`}>
      <input type="date" data-testid={`extend-date-${b.id}`} className="rounded-xl border-2 px-2 py-1.5 text-xs" value={date} onChange={(e) => setDate(e.target.value)} />
      <input data-testid={`extend-msg-${b.id}`} className="rounded-xl border-2 px-2 py-1.5 text-xs flex-1 min-w-[8rem]" placeholder="Message to manager" value={msg} onChange={(e) => setMsg(e.target.value)} />
      <button disabled={!date} data-testid={`extend-submit-${b.id}`} onClick={send} className="rounded-full px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" style={{ background: BRAND.orange }}>Request extension</button>
    </div>
  );
}

function ActiveCard({ b, authFetch, API, onDone }) {
  const p = b.property || {};
  const active = p.assist_services || Object.keys(SERVICES);
  const flag = async (service) => {
    try { await authFetch({ method: "post", url: `${API}/homestay/bookings/${b.id}/assist`, data: { service } }); toast.success(service === "flatmate" ? "Added to Flatmates Connect — we'll match you" : "Manager notified"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not send")); }
  };
  return (
    <div data-testid={`my-active-booking-${b.id}`} className="rounded-3xl border-2 bg-white p-5 shadow-lg" style={{ borderColor: "#16A34A" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {p.id && <BrandMark property={p} size={48} />}
          <div className="min-w-0"><div className="font-display text-2xl font-bold truncate" style={{ color: BRAND.green }}>{p.name}</div><div className="text-xs" style={{ color: BRAND.muted }}>{p.address}</div></div>
        </div>
        <span data-testid={`my-active-badge-${b.id}`} className="rounded-full px-3 py-1 text-xs font-bold text-white inline-flex items-center gap-1 shrink-0" style={{ background: "#16A34A" }}><BadgeCheck className="w-3.5 h-3.5" /> Active</span>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
        <div className="space-y-1">
          <div>Guest ID: <b data-testid={`my-guest-code-${b.id}`}>{b.guest_code || "—"}</b></div>
          <div>Room / Bed: <b data-testid={`my-bed-${b.id}`}>{b.room_name ? `${b.room_name} · ` : ""}{b.bed_label || "TBA"}</b></div>
          <div className="inline-flex items-center gap-1"><CalendarClock className="w-4 h-4" /> {b.check_in?.replace("T", " ").slice(0, 16)} → {b.check_out?.slice(0, 10) || "open"}</div>
          {p.manager_phone && <a href={`tel:${p.manager_phone}`} data-testid={`my-manager-call-${b.id}`} className="flex items-center gap-1 font-bold" style={{ color: BRAND.green }}><Phone className="w-4 h-4" /> Manager: {p.manager_phone}</a>}
        </div>
        <div className="rounded-2xl p-3 text-xs" style={{ background: "#F6F8F7" }}><TaxBreakdown b={b} /></div>
      </div>
      <div className="mt-4 text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>Assist services</div>
      <div className="mt-1.5 flex flex-wrap gap-2" data-testid={`my-assist-panel-${b.id}`}>
        {Object.entries(SERVICES).filter(([k]) => active.includes(k)).map(([k, l]) => {
          const on = b.flags.includes(k);
          return <button key={k} disabled={on} data-testid={`assist-${k}-${b.id}`} onClick={() => flag(k)} className="rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-100" style={{ borderColor: on ? "#16A34A" : BRAND.line, background: on ? "#F0FDF4" : "#fff", color: on ? "#16A34A" : "#111" }}>{l}{on ? " ✓" : ""}</button>;
        })}
      </div>
      {active.includes("extend") && <div className="mt-4"><div className="text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: BRAND.muted }}>Extend stay</div><ExtendBox b={b} authFetch={authFetch} API={API} onDone={onDone} /></div>}
    </div>
  );
}

export function MyBookings({ authFetch, API }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/my-bookings` }).then((r) => setD(r.data)).catch(() => setD({ active: [], history: [] })), [authFetch, API]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="my-bookings">
      {d.active.map((b) => <ActiveCard key={b.id} b={b} authFetch={authFetch} API={API} onDone={load} />)}
      {d.history.length > 0 && (
        <div data-testid="my-booking-history" className="rounded-3xl border-2 bg-white p-5" style={{ borderColor: BRAND.line }}>
          <div className="text-sm font-bold mb-2" style={{ color: BRAND.green }}>Booking history</div>
          {d.history.map((b) => (
            <div key={b.id} data-testid={`my-history-${b.id}`} className="py-2 border-t flex justify-between text-sm" style={{ borderColor: BRAND.line }}>
              <span><b>{b.property?.name}</b><span className="block text-xs" style={{ color: BRAND.muted }}>{b.check_in?.slice(0, 10)} → {b.check_out?.slice(0, 10) || "—"} · {b.bed_label || ""} · {b.guest_code || ""}</span></span>
              <span className="text-xs text-right">{inr(b.amount_inr)}<span className="block uppercase font-bold" style={{ color: BRAND.muted }}>{b.status.replace("_", " ")}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
